import { RefObject, useRef, useState } from "react";
import { useInView } from "@/components/Animation";
import styles from "./Video.module.scss";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import dynamic from "next/dynamic";
import { type MediaPlayerInstance } from "@vidstack/react";
import { HeaderVideosType } from "@/types/PageType";
import { iframeSrcFetch } from "@/utils/iframeSrcFetch";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG";
import Image from "next/image";

const VidstackPlayer = dynamic(
  () => import("../VidstackPlayer/VidstackPlayer"),
);

/* jscpd:ignore-start */
const ReactPlayer = dynamic(() => import("react-player"), {
  ssr: false,
  loading: () => <></>,
});
/* jscpd:ignore-end */

export const Video = ({
  onlineVideo,
  poster,
}: {
  onlineVideo: string;
  poster?: string;
}) => {
  const video_src = iframeSrcFetch(onlineVideo);
  const { getIcon } = useGlobalContext();

  const playIcon = (
    <CustomSVG
      src={getIcon("play")}
      width={83}
      height={83}
      wrapper="span"
      aria-hidden="true"
      className="is-flex"
    />
  );

  return (
    <div className={`${styles["iframe-box"]} iframe-box`}>
      <ReactPlayer
        url={video_src}
        playing
        loop
        controls={true}
        // uncomment below to enable background video and disable controls
        // config={{
        //   vimeo: {
        //     playerOptions: {
        //       background: true,
        //     },
        //   },
        // }}
        light={poster || true}
        playIcon={playIcon ? playIcon : undefined}
        width={"100%"}
        height={"100%"}
        className={styles["video-player"]}
      />
    </div>
  );
};

export const AudioIcon = ({
  isMuted,
  player,
  getIcon,
}: {
  isMuted: boolean;
  player: RefObject<MediaPlayerInstance>;
  getIcon: (key: string) => string;
}) => {
  const { getLabel } = useGlobalContext();
  return (
    <button
      className={`${isMuted ? styles["muted"] : ""} ${styles["audio-btn"]} audio-btn button-reset`}
      onClick={(e) => {
        e.stopPropagation();
        const c = player.current;
        if (c) {
          c.muted = !isMuted;
        }
      }}
      role="switch"
      aria-checked={!isMuted}
      aria-label={
        isMuted ? getLabel("unmute", "Unmute") : getLabel("mute", "Mute")
      }
    >
      <CustomSVG
        width={32}
        height={32}
        src={isMuted ? getIcon("audioOff") : getIcon("audioOn")}
        wrapper="span"
        aria-hidden="true"
        className="is-flex"
      />
    </button>
  );
};

export const InternalVideo = ({
  autoplay,
  loop = true,
  muted: mutedProps,
  video_decorative = false,
  singlePageUrl,
  video,
  headerVideo,
}: {
  autoplay: boolean;
  controls?: boolean;
  loop?: boolean;
  muted: boolean;
  video_decorative?: boolean;
  singlePageUrl?: string;
  video: HeaderVideosType;
  headerVideo?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const playerInView = useInView(ref, { threshold: 0.1, triggerOnce: true });
  const thumb = video.video_thumbnail_desktop;

  return (
    <div ref={ref} className="min-h-px">
      {!playerInView && thumb && thumb.url && (
        <Image
          src={thumb.url}
          alt={thumb.alt}
          width={thumb.width}
          height={thumb.height}
          priority={headerVideo}
          sizes="100vw"
          style={{ width: "100%", height: "auto", display: "block" }}
        />
      )}
      {playerInView && video.internes_video_desktop && (
        <VidstackPlayer
          autoplay={autoplay}
          loop={loop}
          muted={mutedProps}
          video_decorative={video_decorative}
          singlePageUrl={singlePageUrl}
          video={video}
          headerVideo={headerVideo}
        />
      )}
    </div>
  );
};

export const PlayPauseButton = ({
  isPlaying,
  player,
  getIcon,
  setShowcontrols,
  video_decorative = false,
}: {
  isPlaying: boolean;
  player: React.RefObject<MediaPlayerInstance>; // Replace 'any' with your MediaPlayerInstance type if available
  getIcon: (key: string) => string;
  setShowcontrols: React.Dispatch<React.SetStateAction<boolean>>;
  video_decorative?: boolean;
}) => {
  const [playing, setPlaying] = useState(isPlaying);
  const { getLabel } = useGlobalContext();
  const handlePlayPause = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const c = player.current;
    if (c) {
      if (playing) {
        // Assuming your MediaPlayerInstance has a pause method
        c.pause();
        setPlaying(false);
      } else if (c.state.canPlay) {
        // Assuming your MediaPlayerInstance has a play method
        c.play();
        setPlaying(true);
      }

      if (!video_decorative) setShowcontrols(!playing);
    }
  };

  return (
    <button
      className={`${playing ? styles["pause"] : styles["play"]} ${
        styles["play-pause-btn"]
      } button-reset play-pause-btn`}
      onClick={handlePlayPause}
      role="button"
      aria-label={
        playing ? `${getLabel("pause")} Button` : `${getLabel("play")} Button`
      }
    >
      <CustomSVG
        src={playing ? getIcon("pause") : getIcon("play")}
        width={32}
        height={32}
        wrapper="span"
        aria-hidden
        className="is-flex"
      />
    </button>
  );
};
